import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ErrorBox from '../../core/components/ErrorBox';
import PageContainer from '../../core/components/PageContainer';
import ResponsiveActionButton from '../../core/components/ResponsiveActionButton';
import { formatDateForLocale } from '../../core/dateFormatting';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';
import useProperty from '../../property/hooks/useProperty';
import useExpenses, { type Expense } from '../hooks/useExpenses';
import useExpenseCategories from '../hooks/useExpenseCategories';
import useExpenseFilters, { type DatePreset } from '../hooks/useExpenseFilters';
import DateRangeFilter from './DateRangeFilter';
import ExpenseFormDialog from './ExpenseFormDialog';

const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
};

const selectStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        backgroundColor: '#0f0f23',
        '& fieldset': {
            borderWidth: '2px',
            borderColor: '#374151',
        },
        '&:hover fieldset': {
            borderWidth: '2px',
            borderColor: '#6366f1',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '3px',
            borderColor: '#6366f1',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
    },
};

const PRESETS: { value: DatePreset; label: string }[] = [
    { value: 'this-month', label: 'This Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'this-quarter', label: 'This Quarter' },
    { value: 'last-quarter', label: 'Last Quarter' },
    { value: 'this-year', label: 'This Year' },
    { value: 'last-year', label: 'Last Year' },
    { value: 'all-time', label: 'All Time' },
];

const ExpensesPage = () => {
    const propertyId = usePropertyId();
    const pathname = window.location.hash.replace('#', '');

    const isLegacyRoute = pathname === '/expenses';
    const isPropertyRoute = /^\/\d+\/expenses/.test(pathname);

    const { property } = useProperty();
    const currency = property?.currency || 'EUR';

    const { filters, updateFilters, applyPreset, clearFilters } = useExpenseFilters();

    const {
        expenses,
        loading,
        error,
        createExpense,
        updateExpense,
        deleteExpense,
    } = useExpenses(propertyId, filters);

    const { categories, loading: categoriesLoading } = useExpenseCategories(propertyId);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

    const handleOpenDialog = (expense?: Expense) => {
        setSelectedExpense(expense || null);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedExpense(null);
    };

    if (!propertyId) {
        if (isPropertyRoute) {
            return null;
        }
        if (isLegacyRoute) {
            return <PropertySelectionDialog open={true} targetPath="/expenses" />;
        }
        return null;
    }

    if (error) {
        return <ErrorBox error={new Error(error)} />;
    }

    const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontSize: '2rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontWeight: 900,
                    }}
                >
                    Expenses
                </Typography>

                <ResponsiveActionButton
                    label="Add Expense"
                    icon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                />
            </Box>

            <Stack spacing={2} sx={{ mb: 4 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ ...selectStyles, minWidth: 180 }}>
                        <InputLabel id="category-filter-label">Category</InputLabel>
                        <Select
                            labelId="category-filter-label"
                            value={filters.categoryId ?? ''}
                            onChange={(e) => {
                                const value = e.target.value as number | '';
                                updateFilters({ categoryId: value === '' ? undefined : value });
                            }}
                            label="Category"
                            displayEmpty
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ ...selectStyles, minWidth: 150 }}>
                        <InputLabel id="type-filter-label">Type</InputLabel>
                        <Select
                            labelId="type-filter-label"
                            value={filters.type ?? 'all'}
                            onChange={(e) => {
                                updateFilters({ type: e.target.value as 'all' | 'one-time' | 'recurring' });
                            }}
                            label="Type"
                        >
                            <MenuItem value="all">All Types</MenuItem>
                            <MenuItem value="one-time">One-time</MenuItem>
                            <MenuItem value="recurring">Recurring</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ ...selectStyles, minWidth: 150 }}>
                        <InputLabel id="preset-filter-label">Preset</InputLabel>
                        <Select
                            labelId="preset-filter-label"
                            value=""
                            onChange={(e) => {
                                if (e.target.value) {
                                    applyPreset(e.target.value as DatePreset);
                                }
                            }}
                            label="Preset"
                            displayEmpty
                        >
                            <MenuItem value="" disabled>
                                Select preset...
                            </MenuItem>
                            {PRESETS.map((preset) => (
                                <MenuItem key={preset.value} value={preset.value}>
                                    {preset.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <DateRangeFilter
                        label="From"
                        year={filters.fromYear}
                        month={filters.fromMonth}
                        day={filters.fromDay}
                        onYearChange={(year) => updateFilters({ fromYear: year })}
                        onMonthChange={(month) => updateFilters({ fromMonth: month })}
                        onDayChange={(day) => updateFilters({ fromDay: day })}
                    />

                    <DateRangeFilter
                        label="To"
                        year={filters.toYear}
                        month={filters.toMonth}
                        day={filters.toDay}
                        onYearChange={(year) => updateFilters({ toYear: year })}
                        onMonthChange={(month) => updateFilters({ toMonth: month })}
                        onDayChange={(day) => updateFilters({ toDay: day })}
                    />

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={clearFilters}
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.75rem',
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#6366f1',
                            },
                        }}
                    >
                        Clear
                    </Button>
                </Stack>
            </Stack>

            {loading || categoriesLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer
                    sx={{
                        border: '3px solid #374151',
                        borderRadius: 0,
                        backgroundColor: '#1a1a2e',
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow
                                sx={{
                                    backgroundColor: '#1a1a2e',
                                    borderBottom: '3px solid #6366f1',
                                }}
                            >
                                <TableCell
                                    sx={{
                                        fontWeight: 900,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                    }}
                                >
                                    Date
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 900,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                    }}
                                >
                                    Category
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 900,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                    }}
                                >
                                    Description
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 900,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                    }}
                                >
                                    Amount
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 900,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                        width: 80,
                                    }}
                                >
                                    Type
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {expenses.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        sx={{
                                            textAlign: 'center',
                                            py: 6,
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                        }}
                                    >
                                        No expenses found. Click &quot;Add Expense&quot; to create one.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                expenses.map((expense) => (
                                    <TableRow
                                        key={expense.id}
                                        onClick={() => handleOpenDialog(expense)}
                                        sx={{
                                            borderBottom: '1px solid #374151',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                transform: 'translateX(2px)',
                                            },
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {formatDateForLocale(expense.date)}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {expense.category?.name || 'Unknown'}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: 'text.secondary',
                                                maxWidth: 300,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {expense.description || '-'}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#ef4444',
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {formatCurrency(expense.amount, currency)}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                {expense.isRecurring && (
                                                    <Tooltip title={`Recurring: ${expense.recurrenceType}`}>
                                                        <AutorenewIcon
                                                            sx={{
                                                                fontSize: 18,
                                                                color: '#6366f1',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {expense.cleaningId && (
                                                    <Tooltip title="From Cleaning">
                                                        <CleaningServicesIcon
                                                            sx={{
                                                                fontSize: 18,
                                                                color: '#f59e0b',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {expense.isAutoGenerated && !expense.cleaningId && (
                                                    <Box
                                                        sx={{
                                                            px: 1,
                                                            py: 0.25,
                                                            border: '1px solid #6b7280',
                                                            fontSize: '0.625rem',
                                                            fontWeight: 700,
                                                            color: '#6b7280',
                                                            textTransform: 'uppercase',
                                                        }}
                                                    >
                                                        Auto
                                                    </Box>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                        {expenses.length > 0 && (
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: '#0f0f23',
                                        borderTop: '3px solid #ef4444',
                                    }}
                                >
                                    <TableCell
                                        colSpan={3}
                                        sx={{
                                            fontWeight: 900,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        Total
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 900,
                                            color: '#ef4444',
                                            fontFamily: 'monospace',
                                            fontSize: '1rem',
                                        }}
                                    >
                                        {formatCurrency(totalExpenses, currency)}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                        )}
                    </Table>
                </TableContainer>
            )}

            <ExpenseFormDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                expense={selectedExpense}
                categories={categories}
                currency={currency}
                onSave={createExpense}
                onUpdate={updateExpense}
                onDelete={deleteExpense}
            />
        </PageContainer>
    );
};

export default ExpensesPage;
